/**
 * Yotpo initializes the `.yotpo-widget-instance` elements present when its loader
 * runs its first pass. Product cards inserted afterwards (facet filtering, "Load
 * more", product recommendations, Rebuy cards) would stay without stars.
 *
 * This watches the DOM for new star-rating placeholders and initializes each one
 * through the initializer Yotpo created for its first pass (captured by the inline
 * script in layout/theme.liquid). That initializer keeps its bookkeeping, so only
 * the new element is rendered. `yotpoWidgetsContainer.initWidgets()` is NOT used
 * for this: it builds a fresh initializer and re-renders every widget on the page.
 */
(() => {
  const STAR_INSTANCE_ID = '499958';
  const SELECTOR = `.yotpo-widget-instance[data-yotpo-instance-id="${STAR_INSTANCE_ID}"]:not([data-yotpo-element-loaded])`;
  const HANDLED_ATTR = 'data-theme-yotpo-init';
  const FALLBACK_INTERVAL = 3000;

  const pending = new Set();
  let flushTimer = null;
  let lastFallbackAt = 0;

  function yotpoReady() {
    const container = window.yotpoWidgetsContainer;
    return !!(container && typeof container.initWidgets === 'function') && document.readyState !== 'loading';
  }

  function findInitializer() {
    const registry = window.themeYotpoInitializers || {};
    const guids = (window.yotpoWidgetsContainer && window.yotpoWidgetsContainer.guids) || {};
    for (const guid of Object.keys(registry)) {
      const config = guids[guid] && guids[guid].config;
      if (config && config.widgets && config.widgets[STAR_INSTANCE_ID]) return registry[guid];
    }
    return null;
  }

  // Last resort when the initializer could not be captured: Yotpo's public call,
  // rate limited because it re-renders every widget on the page.
  function fallbackGlobalInit() {
    const now = Date.now();
    if (now - lastFallbackAt < FALLBACK_INTERVAL) {
      clearTimeout(flushTimer);
      flushTimer = setTimeout(flush, FALLBACK_INTERVAL - (now - lastFallbackAt));
      return;
    }
    lastFallbackAt = now;
    window.yotpoWidgetsContainer.initWidgets();
  }

  function flush() {
    if (!pending.size) return;
    if (!yotpoReady()) {
      // Yotpo has not run its first pass yet; that pass picks up everything in the DOM.
      pending.clear();
      return;
    }
    const initializer = findInitializer();
    let needsFallback = false;
    pending.forEach((el) => {
      if (!el.isConnected || el.hasAttribute('data-yotpo-element-loaded') || el.hasAttribute(HANDLED_ATTR)) return;
      el.setAttribute(HANDLED_ATTR, '1');
      if (initializer && typeof initializer.initWidgetPlaceholder === 'function') {
        initializer.initWidgetPlaceholder(el);
      } else {
        needsFallback = true;
      }
    });
    pending.clear();
    if (needsFallback && document.querySelector(SELECTOR)) fallbackGlobalInit();
  }

  function schedule() {
    clearTimeout(flushTimer);
    flushTimer = setTimeout(flush, 150);
  }

  function collect(node) {
    if (node.matches(SELECTOR)) pending.add(node);
    node.querySelectorAll(SELECTOR).forEach((el) => pending.add(el));
  }

  const observer = new MutationObserver((mutations) => {
    let found = false;
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue;
        const before = pending.size;
        collect(node);
        if (pending.size !== before) found = true;
      }
    }
    if (found) schedule();
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
